"use client";

/**
 * @file Bottom-anchored, non-blocking legal-consent toast. Implements the
 * timing rules from `docs/plans/legal-copy/legal-copy-spec.md` §5.6:
 *
 *  - eligible for interaction-based acceptance after 3s of visible display,
 *  - qualifying interactions are `pointerdown` | `keydown` | `scroll`
 *    (excluding clicks on this toast's own controls),
 *  - if the page remains visible for the full 10s duration, acceptance is
 *    recorded automatically,
 *  - never records acceptance before the toast has actually rendered.
 *
 * The toast renders to a fixed-position element near the page bottom with
 * `data-slot="legal-consent-toast"` so visual-regression specs can mask it.
 */
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { Link } from "@/i18n/navigation";

import type { LegalConsentDocumentId } from "@wmart/shared-types";

export const LEGAL_CONSENT_TOAST_TOTAL_MS = 10_000;
export const LEGAL_CONSENT_TOAST_INTERACTION_DELAY_MS = 3_000;

interface LegalConsentToastProps {
  staleDocuments: LegalConsentDocumentId[];
  onAccept: () => void;
  onDismiss: () => void;
}

export function LegalConsentToast({
  staleDocuments,
  onAccept,
  onDismiss,
}: LegalConsentToastProps) {
  const titleId = useId();
  const descId = useId();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const interactionEligibleRef = useRef(false);
  const acceptedRef = useRef(false);
  const [, setNow] = useState(0);

  const safeAccept = useCallback(() => {
    if (acceptedRef.current) return;
    acceptedRef.current = true;
    onAccept();
  }, [onAccept]);

  useEffect(() => {
    const t1 = window.setTimeout(() => {
      interactionEligibleRef.current = true;
      setNow((n) => n + 1);
    }, LEGAL_CONSENT_TOAST_INTERACTION_DELAY_MS);
    const t2 = window.setTimeout(safeAccept, LEGAL_CONSENT_TOAST_TOTAL_MS);

    function shouldCount(target: EventTarget | null): boolean {
      if (!interactionEligibleRef.current) return false;
      if (!(target instanceof Node)) return true;
      const root = rootRef.current;
      if (root && root.contains(target)) return false;
      return true;
    }
    function handlePointer(ev: PointerEvent) {
      if (shouldCount(ev.target)) safeAccept();
    }
    function handleKey(ev: KeyboardEvent) {
      if (shouldCount(ev.target)) safeAccept();
    }
    function handleScroll() {
      if (interactionEligibleRef.current) safeAccept();
    }
    window.addEventListener("pointerdown", handlePointer, { capture: true });
    window.addEventListener("keydown", handleKey, { capture: true });
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.removeEventListener("pointerdown", handlePointer, { capture: true });
      window.removeEventListener("keydown", handleKey, { capture: true });
      window.removeEventListener("scroll", handleScroll);
    };
  }, [safeAccept]);

  const firstStaleId = staleDocuments[0];
  // UrlObject form bypasses Next 16's strict RouteImpl<> on the
  // typed-routes union union (same workaround applied to the
  // explicit <Link href="/terms"> calls above).
  const firstStaleHref =
    firstStaleId === "privacy"
      ? { pathname: "/privacy" }
      : firstStaleId === "cookie_policy"
        ? { pathname: "/cookie-policy" }
        : { pathname: "/terms" };

  return (
    <div
      ref={rootRef}
      data-slot="legal-consent-toast"
      role="status"
      aria-live="polite"
      aria-labelledby={titleId}
      aria-describedby={descId}
      className="pointer-events-auto fixed inset-x-4 bottom-4 z-50 mx-auto max-w-xl rounded-md border border-ink-200 bg-ivory p-4 shadow-lg sm:inset-x-auto sm:end-4 sm:start-auto"
    >
      <p id={titleId} className="text-body-sm font-semibold text-ink-900">
        Continued use means acceptance
      </p>
      <p id={descId} className="mt-1 text-body-sm text-ink-700">
        By continuing to use WMart, you accept our{" "}
        <Link href={{ pathname: "/terms" }} className="underline">
          Terms
        </Link>
        ,{" "}
        <Link href={{ pathname: "/privacy" }} className="underline">
          Privacy Policy
        </Link>
        , and{" "}
        <Link href={{ pathname: "/cookie-policy" }} className="underline">
          Cookie Policy
        </Link>
        .
      </p>
      <div className="mt-3 flex items-center gap-3">
        <Link
          href={firstStaleHref}
          className="rounded-sm bg-ink-900 px-3 py-1.5 text-body-sm font-medium text-ivory hover:bg-ink-800"
        >
          View documents
        </Link>
        <button
          type="button"
          onClick={onDismiss}
          className="rounded-sm px-2 py-1 text-body-sm text-ink-600 hover:text-ink-900"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}
